import { tap, delay, take } from "rxjs/operators";
import { Injectable, EventEmitter, ViewContainerRef, ComponentRef, Inject, inject } from "@angular/core";
import { PlatformLocation } from "@angular/common";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { BpModalCanDismiss } from "@models/interfaces/bp-modal-can-dismiss";
import { BpModalDismiss } from "@models/interfaces/bp-modal-dismiss";
import { isSafari } from "@helpers/utils";
import { Router } from "@angular/router";

// TODO: eslint fix
/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable({
	providedIn: "root"
})
export class ModalService {
	public name: string = "ModalService";
	public stack: Modal[] = [];
	public modalSubject: BehaviorSubject<Modal> = new BehaviorSubject<Modal>(null);
	public ignoreBack: boolean = false;
	public router = inject(Router);
	public modalDismissed$: Subject<void> = new Subject<void>();

	public constructor(
		private location: PlatformLocation,
		@Inject("ModalGuardService") private guard: CanCreate
	) {
		location.onPopState(() => {
			if (this.ignoreBack) {
				this.ignoreBack = false;
				return;
			}
			if (this.getCurrentModal()) {
				this.getCurrentModal().dismiss({}, "back-button");
			}
		});
	}

	public hideAll(): void {
		this.stack.forEach((modal) => modal.hide());
	}

	public unhideAll(): void {
		this.stack.forEach((modal) => modal.unhide());
	}

	public pop(data: any = null, role: string = ""): void {
		if (!this.stack[this.stack.length - 1] || this.stack.length === 0) {
			return;
		}
		this.stack[this.stack.length - 1].dismiss(data, role);
	}

	public create<T = any>(component: new (...args) => T, inputs = null, options: ModalOptions = null): Modal<T> {
		if (this.guard.canCreate(component, inputs, options)) {
			const modal = new Modal<T>(component, inputs, options);
			modal.onDismiss
				.pipe(
					tap((event) => {
						if (options && options.backButtonRoleDismiss) {
							return;
						}

						if (event.role !== "back-button") {
							this.ignoreBack = true;
						}
					}),
					delay(300)
				)
				.subscribe((event) => {
					this.modalDismissed$.next();
					this.removeFromStack(modal);
					this.modalSubject.next(this.getCurrentModal());
					this.restoreFocus(event);
				});

			modal.onShown.pipe(delay(0)).subscribe(() => {
				this.stack = [...this.stack, modal];
				this.modalSubject.next(modal);

				this.location.pushState(
					modal.component.name,
					modal.component.name,
					this.location.pathname + this.location.search
				);
			});
			return modal;
		}

		return this.guard.createLockModal(this);
	}

	private restoreFocus(event: { data: any; role: string }): void {
		if (
			this.getCurrentModal() &&
			event.data === false &&
			event.role === "cancel" &&
			typeof this.getCurrentModal().instance.doTrapFocusTime === "function"
		) {
			this.getCurrentModal().instance.doTrapFocusTime();
		}
	}

	private get _currentModal(): Modal<any> {
		if (this.stack.length === 0) {
			return null;
		}
		return this.stack[this.stack.length - 1];
	}

	public removeFromStack(modal: Modal): void {
		this.stack = this.stack.filter((m: Modal) => m !== modal);
	}

	public push(modal: Modal): void {
		this.stack.push(modal);
		this.modalSubject.next(modal);
		this.location.pushState(
			modal.component.name,
			modal.component.name,
			this.location.pathname + this.location.search
		);
	}

	public popAll(): void {
		this.stack.forEach((modal: Modal) => modal.dismiss());
	}

	public showModal(n: any, data: any = null, options: ModalOptions = null): Modal {
		return this.create(n, { data }, options).present();
	}

	public getCurrentModal(): Modal {
		return this._currentModal;
	}

	public popModalStack(componentInstance, data = null, role: string = ""): void {
		this.pop(data, role);
	}
}

export class Modal<T = any> {
	public constructor(
		public component: new (...args) => T,
		public inputs,
		options: ModalOptions = {}
	) {
		this.options = { ...this.options, ...options };
	}

	public options: ModalOptions = {
		showBackdrop: true,
		backdropDismiss: true,
		// @backdropDismiss: Seems to be not implemented, but as long as it's set on various places there
		// is a new property `preventBackdropDismiss` to handle new case and to not touch prod code.
		showCloseButton: false,
		noPadding: true,
		preventBackdropDismiss: false,
		preventEscDismiss: false
	};
	public onShown: EventEmitter<void> = new EventEmitter<void>();
	public afterViewInit: EventEmitter<T> = new EventEmitter<T>();

	public onDismiss: EventEmitter<{ data: any; role: string }> = new EventEmitter<{ data: any; role: string }>();
	private _isShowing: boolean = false;
	private _componentRef: ComponentRef<any>;

	public get instance(): T {
		return this._componentRef.instance;
	}

	public get isShowing(): boolean {
		return this._isShowing;
	}

	public present(): Modal<T> {
		this.onShown.emit();
		this.onShown.complete();
		if (!isSafari()) {
			setTimeout(() => (this._isShowing = true));
		} else {
			setTimeout(() => (this._isShowing = true), 500);
		}
		return this;
	}

	public dismiss(data: any = null, role: string = null): void {
		if (typeof this._componentRef.instance.modalCanDismiss === "function") {
			const canDismiss = (this._componentRef.instance as BpModalCanDismiss).modalCanDismiss();
			if ((canDismiss as any).subscribe) {
				(canDismiss as Observable<boolean>).pipe(take(1)).subscribe((b) => {
					if (b) {
						this._dismiss(data, role);
					}
				});
			} else {
				if (canDismiss) {
					this._dismiss(data, role);
				}
			}
		} else {
			this._dismiss(data, role);
		}
	}

	private _dismiss(data: any, role: string): void {
		this._isShowing = false;
		this.onDismiss.emit({ data, role });
		this._componentRef.destroy();
		this.onDismiss.complete();
	}

	public materialize(containerRef: ViewContainerRef): void {
		if (this._componentRef) {
			this._isShowing = true;
			this._componentRef.location.nativeElement.hidden = false;
			return;
		}

		this._componentRef = containerRef.createComponent(this.component as any);

		Object.assign(this._componentRef.instance, this.inputs);
		if (typeof this._componentRef.instance.onModalDismiss === "function") {
			this.onDismiss.subscribe((params) =>
				(this._componentRef.instance as BpModalDismiss).onModalDismiss(params.data, params.role)
			);
		}
		this.afterViewInit.emit(this._componentRef.instance);
		this.afterViewInit.complete();
	}

	public hide(): void {
		this._isShowing = false;
		this._componentRef.location.nativeElement.hidden = true;
	}

	public unhide(): void {
		this._isShowing = true;
		this._componentRef.location.nativeElement.hidden = false;
	}
}

export interface ModalOptions {
	showBackdrop?: boolean;
	backdropDismiss?: boolean;
	// @backdropDismiss: Seems to be not implemented, but as long as it's set on various places there
	// is a new property `preventBackdropDismiss` to handle new case and to not touch prod code.
	preventBackdropDismiss?: boolean;
	preventEscDismiss?: boolean;
	backButtonRoleDismiss?: boolean;
	title?: string;
	showCloseButton?: boolean;
	subheader?: string;
	noPadding?: boolean;
	customPath?: string;
}

export interface CanCreate {
	canCreate(component: any, inputs: any, options: ModalOptions): boolean;

	createLockModal(modalService: ModalService): Modal;
}
